/**
 * Network Diagram layout rules.
 *
 * The diagram is split into three AREAS, chosen from the asset's activity / transit
 * direction, and each area is split into COLUMNS:
 *
 *   atSource  | col 0 = grid power | col 1 = charging station | col 2 = Ess/Charger | col 3 = Machine/Ghost
 *   atTransit | col 0 (everything shares one column)
 *   atSite    | col 0 = Ess/Charger | col 1 = Machine/Ghost
 *
 * Column 2 assets supply energy and register their row; column 3 assets are being charged
 * and line up on their provider's row.
 *
 * To add a new asset type, add one row to ASSET_LAYOUT_REGISTRY saying which area and
 * column it belongs to. Mark it `providesCharging` if other assets charge from it.
 */
import { NodeIconType } from "../../components/CustomNodes/CustomNodeEnums";
export var AssetTransitDirection;
(function (AssetTransitDirection) {
    AssetTransitDirection["AT_SITE"] = "ATSITE";
    AssetTransitDirection["AT_SOURCE"] = "ATSOURCE";
    AssetTransitDirection["TOWARDS_SITE"] = "TOWARDSSITE";
    AssetTransitDirection["TOWARDS_SOURCE"] = "TOWARDSSOURCE";
})(AssetTransitDirection || (AssetTransitDirection = {}));
export const NetworkArea = {
    AT_SITE: "atSite",
    AT_SOURCE: "atSource",
    AT_TRANSIT: "atTransit"
};
/** x pixel for each column index within an area. */
const AREA_COLUMN_X = {
    atSite: [850, 1050],
    atSource: [100, 250, 450, 543],
    atTransit: [670]
};
export const NetworkColumn = {
    CHARGING_STATION: AREA_COLUMN_X.atSource[1],
    POWER: AREA_COLUMN_X.atSource[0],
    SITE: AREA_COLUMN_X.atSite[0],
    SOURCE: AREA_COLUMN_X.atSource[2],
    TRANSIT: AREA_COLUMN_X.atTransit[0],
    WORK_AREA: AREA_COLUMN_X.atSite[1]
};
const ROW_STEP = 120;
const STATION_ROW_STEP = 270;
// A charged asset whose provider row cannot be resolved sits slightly right of the aligned column.
const UNRESOLVED_PROVIDER_X = AREA_COLUMN_X.atSource[2] + 100;
const PROVIDER_PLACEMENTS = [
    { area: NetworkArea.AT_SOURCE, column: 2 },
    { area: NetworkArea.AT_SITE, column: 0 }
];
/** Declare a new asset type here — one row is enough for it to be laid out correctly. */
export const ASSET_LAYOUT_REGISTRY = {
    CHARGER: { defaultArea: NetworkArea.AT_SOURCE, placements: PROVIDER_PLACEMENTS, providesCharging: true },
    CHARGING_STATION: { kind: "station" },
    DIESEL_GENERATOR: { kind: "utility" },
    ENERGY_SOURCE: {
        defaultArea: NetworkArea.AT_SOURCE,
        icon: NodeIconType.METHANOL_REFORMER,
        legacyDoubleTransitSlot: true,
        placements: PROVIDER_PLACEMENTS
    },
    ESS: { defaultArea: NetworkArea.AT_SOURCE, placements: PROVIDER_PLACEMENTS, providesCharging: true },
    GHOST: {
        defaultArea: NetworkArea.AT_SITE,
        icon: NodeIconType.GHOST_MACHINE,
        placements: [
            { alignToChargingSource: true, area: NetworkArea.AT_SOURCE, column: 3 },
            { alignToChargingSource: true, area: NetworkArea.AT_SITE, column: 1 }
        ],
        skipsTransitArea: true
    },
    MACHINE: {
        placements: [
            { alignToChargingSource: true, area: NetworkArea.AT_SOURCE, column: 3 },
            { alignToChargingSource: true, area: NetworkArea.AT_SITE, column: 1 }
        ]
    }
};
// Unrecognized types are laid out like a provider so a brand-new assetSourceType still
// lands in a real column instead of being rendered at an undefined position.
const FALLBACK_RULE = {
    defaultArea: NetworkArea.AT_SOURCE,
    placements: PROVIDER_PLACEMENTS,
    providesCharging: true
};
export const getLayoutRule = (assetSourceType) => ASSET_LAYOUT_REGISTRY[assetSourceType?.toUpperCase() ?? ""] ?? FALLBACK_RULE;
const laneFor = (area, column) => {
    if (area === NetworkArea.AT_TRANSIT)
        return "transit";
    if (area === NetworkArea.AT_SITE)
        return column === 0 ? "site" : "workArea";
    return "source";
};
export const createLaneAllocator = () => {
    const cursors = { site: 100, source: 100, station: 80, transit: 150, workArea: 100 };
    // atTransit never registers rows, so the record is intentionally partial.
    const rows = { atSite: new Map(), atSource: new Map() };
    const takenWorkAreaRows = [];
    const takenChargedSourceRows = [];
    return {
        registerRow: (area, assetId, y) => rows[area]?.set(assetId, y),
        rowOf: (area, chargingSource) => {
            if (!chargingSource || chargingSource.toUpperCase() === "OTHERS")
                return null;
            return rows[area]?.get(chargingSource) ?? null;
        },
        take: (lane) => {
            const y = cursors[lane];
            cursors[lane] += lane === "station" ? STATION_ROW_STEP : ROW_STEP;
            return y;
        },
        /** Consumers sharing one provider stack downwards instead of landing on the same row. */
        takeChargedSourceRow: (providerRow) => {
            let y = providerRow;
            while (takenChargedSourceRows.includes(y))
                y += ROW_STEP;
            takenChargedSourceRows.push(y);
            return y;
        },
        /**
         * Work Area rows must never collide. When a preferred row is given (the charging
         * source's row) the shared cursor is intentionally left untouched.
         */
        takeWorkAreaRow: (preferredRow) => {
            let y;
            if (preferredRow === null) {
                while (takenWorkAreaRows.includes(cursors.workArea))
                    cursors.workArea += ROW_STEP;
                y = cursors.workArea;
                cursors.workArea += ROW_STEP;
            }
            else {
                y = preferredRow;
                while (takenWorkAreaRows.includes(y))
                    y += ROW_STEP;
            }
            takenWorkAreaRows.push(y);
            return y;
        }
    };
};
const resolveArea = (rule, direction, isTransporting) => {
    if (isTransporting && !rule.skipsTransitArea)
        return NetworkArea.AT_TRANSIT;
    if (direction === AssetTransitDirection.AT_SITE)
        return NetworkArea.AT_SITE;
    if (direction === AssetTransitDirection.AT_SOURCE)
        return NetworkArea.AT_SOURCE;
    if (direction === AssetTransitDirection.TOWARDS_SOURCE && rule.defaultArea)
        return NetworkArea.AT_SOURCE;
    return rule.defaultArea;
};
/**
 * Column 3 at source holds assets being charged: they sit beside their provider when its
 * row is known, and shift right when it is not.
 *
 * Relies on providers being positioned first — `sortNetworkData` guarantees that ordering.
 */
const resolveChargedSourcePosition = (chargingSource, lanes) => {
    if (!chargingSource) {
        return { x: NetworkColumn.SOURCE, y: lanes.take("source") };
    }
    const providerRow = lanes.rowOf(NetworkArea.AT_SOURCE, chargingSource);
    return providerRow === null
        ? { x: UNRESOLVED_PROVIDER_X, y: lanes.take("source") }
        : { x: AREA_COLUMN_X.atSource[3], y: lanes.takeChargedSourceRow(providerRow) };
};
/** Returns null for assets that are deliberately not placed on the canvas. */
export const resolveAssetPosition = ({ assetId, chargingSource, isTransporting, transitDirection }, rule, lanes) => {
    if (rule.kind === "station" || rule.kind === "utility")
        return null;
    // A transporting asset that skips the transit area still burns its transit slot.
    if (isTransporting && rule.skipsTransitArea)
        lanes.take("transit");
    const area = resolveArea(rule, transitDirection?.toUpperCase(), isTransporting);
    if (!area)
        return null;
    if (area === NetworkArea.AT_TRANSIT) {
        if (rule.legacyDoubleTransitSlot)
            lanes.take("transit");
        return { x: NetworkColumn.TRANSIT, y: lanes.take("transit") };
    }
    const placement = rule.placements?.find((candidate) => candidate.area === area);
    if (!placement)
        return null;
    if (placement.alignToChargingSource) {
        if (area === NetworkArea.AT_SOURCE)
            return resolveChargedSourcePosition(chargingSource, lanes);
        return {
            x: AREA_COLUMN_X[area][placement.column],
            y: lanes.takeWorkAreaRow(lanes.rowOf(area, chargingSource))
        };
    }
    const y = lanes.take(laneFor(area, placement.column));
    if (rule.providesCharging)
        lanes.registerRow(area, assetId, y);
    return { x: AREA_COLUMN_X[area][placement.column], y };
};
/**
 * Icon precedence, highest first: charger state, registry icon, wheel-loader product
 * family, then the generic moving/stationary icon.
 */
export const resolveNodeIcon = ({ hasWheelLoaderFamily, isCharger, isIdle, isMachine, isTransporting, isXosCharger }, rule) => {
    if (rule.kind === "station" || rule.kind === "utility")
        return undefined;
    if (isCharger) {
        if (isXosCharger)
            return NodeIconType.ESS;
        return isIdle ? NodeIconType.IDLEMECCHARGER : NodeIconType.CHARGEMECCHARGER;
    }
    if (rule.icon !== undefined)
        return rule.icon;
    if (hasWheelLoaderFamily)
        return NodeIconType.WHEELLOADER;
    if (isMachine)
        return NodeIconType.EXCAVATOR;
    return isTransporting ? NodeIconType.TRUCKWITHESS : NodeIconType.ESS;
};
